import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { FileText, Plus, Edit, Trash2, Download } from "lucide-react";
import { exportToExcel } from "../lib/excel";
import { API } from "../lib/api";
import { sortByName } from "../lib/sortByName";
import { SearchableSelect } from "../components/SearchableSelect";
import { DatePicker } from "../components/DatePicker";
import { useConfirm } from "../components/ConfirmDialog";

const Precos = () => {
  const [precos, setPrecos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [funcoes, setFuncoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, confirmDialog] = useConfirm();
  const [formData, setFormData] = useState({
    cliente_id: '',
    funcao_id: '',
    valor_pagar_padrao: '',
    valor_cobrar_padrao: '',
    adicional_fds_feriado: '',
    adicional_noturno: '',
    vigente_desde: new Date().toISOString().split('T')[0],
    vigente_ate: '',
    observacoes: ''
  });
  const [editingId, setEditingId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  // Estados para filtros
  const [filtros, setFiltros] = useState({
    cliente: 'todos',
    funcao: 'todos'
  });

  const fetchData = async () => {
    try {
      const [precosRes, clientesRes, funcoesRes] = await Promise.all([
        axios.get(`${API}/precos`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/funcoes`)
      ]);
      
      setPrecos(precosRes.data);
      setClientes(clientesRes.data.filter(c => c.status === 'ativo'));
      // Ordenar funções alfabeticamente por nome
      const funcoesAtivas = funcoesRes.data
        .filter(f => f.status === 'ativo')
        .sort(sortByName);
      setFuncoes(funcoesAtivas);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const carregar = async () => { await fetchData(); };
    carregar();
  }, []);

  // Função para filtrar preços
  const getPrecosFiltrados = () => {
    return precos.filter(preco => {
      // Filtro por cliente
      if (filtros.cliente !== 'todos' && preco.cliente_id !== filtros.cliente) {
        return false;
      }
      
      // Filtro por função
      if (filtros.funcao !== 'todos' && preco.funcao_id !== filtros.funcao) {
        return false;
      }
      
      return true;
    });
  };

  // Função para obter nome do cliente
  const getClienteNome = (clienteId) => {
    const cliente = clientes.find(c => c.id === clienteId);
    return cliente ? cliente.nome : 'Cliente não encontrado';
  };

  // Função para obter nome da função
  const getFuncaoNome = (funcaoId) => {
    const funcao = funcoes.find(f => f.id === funcaoId);
    return funcao ? funcao.nome : 'Função não encontrada';
  };

  const exportarPrecosExcel = () => {
    const lista = getPrecosFiltrados();
    if (lista.length === 0) {
      toast.error("Nenhum preço para exportar");
      return;
    }
    const rows = lista.map(preco => ({
      'Cliente': getClienteNome(preco.cliente_id),
      'Função': getFuncaoNome(preco.funcao_id),
      'Valor Pagar': Number((preco.valor_pagar_padrao || 0).toFixed(2)),
      'Valor Cobrar': Number((preco.valor_cobrar_padrao || 0).toFixed(2)),
      'Margem': Number(((preco.valor_cobrar_padrao || 0) - (preco.valor_pagar_padrao || 0)).toFixed(2)),
      'Adicional FDS/Feriado': Number((preco.adicional_fds_feriado || 0).toFixed(2)),
      'Adicional Noturno': Number((preco.adicional_noturno || 0).toFixed(2)),
      'Vigente Desde': preco.vigente_desde ? new Date(preco.vigente_desde + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'Vigente Até': preco.vigente_ate ? new Date(preco.vigente_ate + 'T00:00:00').toLocaleDateString('pt-BR') : '',
      'Observações': preco.observacoes || '',
    }));
    exportToExcel(rows, `precos-${new Date().toISOString().split('T')[0]}`, 'Preços');
    toast.success("Preços exportados para Excel com sucesso!");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validações básicas
    if (!formData.cliente_id || !formData.funcao_id) {
      toast.error("Cliente e Função são obrigatórios");
      return;
    }
    
    if (!formData.valor_pagar_padrao || !formData.valor_cobrar_padrao) {
      toast.error("Valores de pagar e cobrar são obrigatórios");
      return;
    }

    try {
      const submitData = {
        ...formData,
        valor_pagar_padrao: parseFloat(formData.valor_pagar_padrao),
        valor_cobrar_padrao: parseFloat(formData.valor_cobrar_padrao),
        adicional_fds_feriado: formData.adicional_fds_feriado ? parseFloat(formData.adicional_fds_feriado) : 0,
        adicional_noturno: formData.adicional_noturno ? parseFloat(formData.adicional_noturno) : 0,
        vigente_ate: formData.vigente_ate || null
      };

      if (editingId) {
        await axios.put(`${API}/precos/${editingId}`, submitData);
        toast.success("Preço atualizado com sucesso!");
      } else {
        await axios.post(`${API}/precos`, submitData);
        toast.success("Preço criado com sucesso!");
      }
      
      setFormData({
        cliente_id: '',
        funcao_id: '',
        valor_pagar_padrao: '',
        valor_cobrar_padrao: '',
        adicional_fds_feriado: '',
        adicional_noturno: '',
        vigente_desde: new Date().toISOString().split('T')[0],
        vigente_ate: '',
        observacoes: ''
      });
      setEditingId(null);
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao salvar preço");
    }
  };

  const handleEdit = (preco) => {
    setFormData({
      cliente_id: preco.cliente_id,
      funcao_id: preco.funcao_id,
      valor_pagar_padrao: preco.valor_pagar_padrao.toString(),
      valor_cobrar_padrao: preco.valor_cobrar_padrao.toString(),
      adicional_fds_feriado: preco.adicional_fds_feriado?.toString() || '',
      adicional_noturno: preco.adicional_noturno?.toString() || '',
      vigente_desde: preco.vigente_desde,
      vigente_ate: preco.vigente_ate || '',
      observacoes: preco.observacoes || ''
    });
    setEditingId(preco.id);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    const ok = await confirm({
      title: "Excluir preço?",
      description: "Tem certeza que deseja deletar este preço? Esta ação não pode ser desfeita.",
      confirmText: "Excluir",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/precos/${id}`);
      toast.success("Preço deletado com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao deletar preço");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      {confirmDialog}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Tabela de Preços</h1>
          <p className="text-gray-600 dark:text-gray-300">Gerencie os valores por cliente e função</p>
        </div>
        
        <div className="flex gap-2 no-print">
          <Button variant="outline" onClick={exportarPrecosExcel} data-testid="exportar-precos-excel-btn">
            <Download className="mr-2 h-4 w-4" />
            Exportar Excel
          </Button>
          <Button variant="outline" onClick={() => window.print()}>
            <FileText className="mr-2 h-4 w-4" />
            Imprimir
          </Button>
          
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setFormData({
                  cliente_id: '',
                  funcao_id: '',
                  valor_pagar_padrao: '',
                  valor_cobrar_padrao: '',
                  adicional_fds_feriado: '',
                  adicional_noturno: '',
                  vigente_desde: new Date().toISOString().split('T')[0],
                  vigente_ate: '',
                  observacoes: ''
                });
                setEditingId(null);
              }}>
                <Plus className="mr-2 h-4 w-4" />
                Novo Preço
              </Button>
            </DialogTrigger>
          <DialogContent className="sm:max-w-[700px]">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Editar' : 'Novo'} Preço</DialogTitle>
              <DialogDescription>
                Configure os valores para a combinação cliente + função.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cliente_id">Cliente *</Label>
                  <SearchableSelect
                    options={clientes.map(cliente => ({
                      value: cliente.id,
                      label: cliente.nome
                    }))}
                    value={formData.cliente_id}
                    onValueChange={(value) => setFormData({...formData, cliente_id: value})}
                    placeholder="Selecione um cliente"
                    searchPlaceholder="Buscar cliente..."
                    emptyText="Nenhum cliente encontrado"
                    className="w-full"
                  />
                </div>
                <div>
                  <Label htmlFor="funcao_id">Função *</Label>
                  <Select value={formData.funcao_id} onValueChange={(value) => setFormData({...formData, funcao_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma função" />
                    </SelectTrigger>
                    <SelectContent>
                      {funcoes.map((funcao) => (
                        <SelectItem key={funcao.id} value={funcao.id}>
                          {funcao.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="valor_pagar_padrao">Valor a Pagar (R$) *</Label>
                  <Input
                    id="valor_pagar_padrao"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_pagar_padrao}
                    onChange={(e) => setFormData({...formData, valor_pagar_padrao: e.target.value})}
                    placeholder="0,00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="valor_cobrar_padrao">Valor a Cobrar (R$) *</Label>
                  <Input
                    id="valor_cobrar_padrao"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.valor_cobrar_padrao}
                    onChange={(e) => setFormData({...formData, valor_cobrar_padrao: e.target.value})}
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="adicional_fds_feriado">Adicional FDS/Feriado (R$)</Label>
                  <Input
                    id="adicional_fds_feriado"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.adicional_fds_feriado}
                    onChange={(e) => setFormData({...formData, adicional_fds_feriado: e.target.value})}
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <Label htmlFor="adicional_noturno">Adicional Noturno (R$)</Label>
                  <Input
                    id="adicional_noturno"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.adicional_noturno}
                    onChange={(e) => setFormData({...formData, adicional_noturno: e.target.value})}
                    placeholder="0,00"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vigente_desde">Vigente Desde *</Label>
                  <DatePicker
                    id="vigente_desde"
                    value={formData.vigente_desde}
                    onChange={(value) => setFormData({...formData, vigente_desde: value})}
                    placeholder="Selecione a data"
                  />
                </div>
                <div>
                  <Label htmlFor="vigente_ate">Vigente Até</Label>
                  <DatePicker
                    id="vigente_ate"
                    value={formData.vigente_ate}
                    onChange={(value) => setFormData({...formData, vigente_ate: value})}
                    placeholder="Opcional"
                    minDate={formData.vigente_desde}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="observacoes">Observações</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações sobre este preço"
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit">
                  {editingId ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filtros */}
      <Card className="no-print">
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <Label htmlFor="filtro-cliente">Cliente</Label>
              <SearchableSelect
                options={[
                  { value: 'todos', label: 'Todos os clientes' },
                  ...clientes
                    .sort(sortByName)
                    .map(cliente => ({
                      value: cliente.id,
                      label: cliente.nome
                    }))
                ]}
                value={filtros.cliente}
                onValueChange={(value) => setFiltros({...filtros, cliente: value})}
                placeholder="Todos os clientes"
                searchPlaceholder="Buscar cliente..."
                emptyText="Nenhum cliente encontrado"
                className="w-full"
              />
            </div>
            
            <div>
              <Label htmlFor="filtro-funcao">Função</Label>
              <SearchableSelect
                options={[
                  { value: 'todos', label: 'Todas as funções' },
                  ...funcoes
                    .sort(sortByName)
                    .map(funcao => ({
                      value: funcao.id,
                      label: funcao.nome
                    }))
                ]}
                value={filtros.funcao}
                onValueChange={(value) => setFiltros({...filtros, funcao: value})}
                placeholder="Todas as funções"
                searchPlaceholder="Buscar função..."
                emptyText="Nenhuma função encontrada"
                className="w-full"
              />
            </div>
            
            <div>
              <Button 
                variant="outline" 
                onClick={() => setFiltros({ cliente: 'todos', funcao: 'todos' })}
              >
                Limpar Filtros
              </Button>
            </div>
          </div>
          {(filtros.cliente !== 'todos' || filtros.funcao !== 'todos') && (
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-2">
              Mostrando {getPrecosFiltrados().length} de {precos.length} preços
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Função</TableHead>
                <TableHead>Valor Pagar</TableHead>
                <TableHead>Valor Cobrar</TableHead>
                <TableHead>Margem</TableHead>
                <TableHead>Vigente Desde</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {getPrecosFiltrados().map((preco) => (
                <TableRow key={preco.id}>
                  <TableCell className="font-medium">{getClienteNome(preco.cliente_id)}</TableCell>
                  <TableCell>{getFuncaoNome(preco.funcao_id)}</TableCell>
                  <TableCell className="text-red-600 dark:text-red-400">{formatCurrency(preco.valor_pagar_padrao)}</TableCell>
                  <TableCell className="text-green-600 dark:text-green-400">{formatCurrency(preco.valor_cobrar_padrao)}</TableCell>
                  <TableCell className="font-medium">
                    {formatCurrency(preco.valor_cobrar_padrao - preco.valor_pagar_padrao)}
                  </TableCell>
                  <TableCell>{new Date(preco.vigente_desde).toLocaleDateString('pt-BR')}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button size="sm" variant="outline" onClick={() => handleEdit(preco)} data-testid={`editar-preco-${preco.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(preco.id)} data-testid={`excluir-preco-${preco.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Precos;
